import React, { useRef, useEffect, useState } from 'react';
import './CameraComponent.scss';

interface CameraComponentProps {
  isCameraActive: boolean;
  onDeactivateCamera: () => void;
  triggerAnalysis: (file: File) => void;
}

const CameraComponent: React.FC<CameraComponentProps> = ({
  isCameraActive,
  onDeactivateCamera,
  triggerAnalysis,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [capturedImage, setCapturedImage] = useState<File | null>(null);
  const [isLandscape, setIsLandscape] = useState<boolean>(
    window.innerWidth > window.innerHeight,
  );

  const requestFullscreenInLandscape = () => {
    // Verificar si está en modo horizontal
    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    const isAndroid = /Android/i.test(navigator.userAgent);
  
    if (isLandscape && isAndroid) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        (document.documentElement as any).webkitRequestFullscreen();
      } else if ((document.documentElement as any).msRequestFullscreen) {
        (document.documentElement as any).msRequestFullscreen();
      }
    }
  };
  
  // Escuchar cambios de orientación
  window.addEventListener('orientationchange', () => {
    setTimeout(requestFullscreenInLandscape, 100); // pequeño delay para asegurar que la orientación ya cambió
  });

  useEffect(() => {
    const checkOrientation = () => {
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
    };

    window.addEventListener('resize', checkOrientation);
    checkOrientation();

    return () => {
      window.removeEventListener('resize', checkOrientation);
    };
  }, []);

  useEffect(() => {
    if (isCameraActive) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [isCameraActive]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  const handleDeactivateCamera = () => {
    stopCamera(); // Detener la cámara
    onDeactivateCamera(); // Llamar a la función proporcionada por las props
  };

  const capturePhoto = () => {
    if (!canvasRef.current || !videoRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const isPortrait = video.videoHeight > video.videoWidth;

    canvas.width = isPortrait ? video.videoHeight : video.videoWidth;
    canvas.height = isPortrait ? video.videoWidth : video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    if (isPortrait) {
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate(-90 * (Math.PI / 180));
      context.drawImage(
        video,
        -video.videoWidth / 2,
        -video.videoHeight / 2,
        video.videoWidth,
        video.videoHeight,
      );
    } else {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    // Genera un blob a partir del canvas
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Convierte el blob en un objeto File
          const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
          setCapturedImage(file);
          console.log(capturedImage);

          // Ejecuta triggerAnalysis con el archivo generado
          triggerAnalysis(file);

          // Cerrar la cámara después de capturar la imagen
          handleDeactivateCamera();
        }
      },
      'image/jpeg',
      0.8, // Calidad de la imagen
    );
  };

  if (!isCameraActive) {
    return null; // No se muestra nada si la cámara está inactiva
  }

  return (
    <div className="camera-fullscreen">
      <div className="camera-preview">
        <video ref={videoRef} className="video" autoPlay playsInline />
        <div className="focus-frame"></div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
      <div className="controls">
        <button className="capture-button" onClick={capturePhoto}>
          Capturar
        </button>
        <button className="back-button" onClick={handleDeactivateCamera}>
          Volver
        </button>
      </div>
      {!isLandscape && (
        <div className="orientation-alert">
          <div className="alert-backdrop" />
          <div className="alert-message">
            <p>
              Por favor, coloque su dispositivo en modo horizontal para tomar la
              foto.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraComponent;
